import * as React from "react";
import { VideoCatalystStatus, VideoProjectCoachConfig, VideoProjectPlayerConfigRequest } from "@/interfaces";
import { useEffect, useMemo } from "react";
import { VideoProjectServices } from "@/services";
import { Button, Dialog, Field, Input } from "@/components/ui";
import { toast } from "sonner";

interface OwnProps {
  playerId: number | undefined;
  vcStatus: VideoCatalystStatus | undefined;
  coachVCConfig: VideoProjectCoachConfig | undefined;
  open: boolean;
  onUpdateStatus: (status: VideoCatalystStatus) => void;
  onClose: () => void;
}

type Props = OwnProps;

const VideoCreditsModalFC: React.FC<Props> = ({ playerId, vcStatus, coachVCConfig, open, onUpdateStatus, onClose }) => {
  const [configLoading, setConfigLoading] = React.useState(false);
  const [editableCredits, setEditableCredits] = React.useState<number>();

  useEffect(() => {
    setEditableCredits(vcStatus?.creditsAllotted || 0);
  }, [vcStatus]);

  const showSaveCredits = useMemo(() => {
    return !vcStatus?.unlimitedCredits;
  }, [vcStatus?.unlimitedCredits]);

  const canSaveCredits = useMemo(() => {
    return (
      !vcStatus?.unlimitedCredits && editableCredits && coachVCConfig?.defaultPlayerVideoCredits !== editableCredits
    );
  }, [coachVCConfig?.defaultPlayerVideoCredits, editableCredits, vcStatus?.unlimitedCredits]);

  const canResetCredits = useMemo(() => {
    const currentCredits = vcStatus?.creditsAllotted || 0;
    return vcStatus?.unlimitedCredits || coachVCConfig?.defaultPlayerVideoCredits !== currentCredits;
  }, [vcStatus?.unlimitedCredits, coachVCConfig?.defaultPlayerVideoCredits, vcStatus?.creditsAllotted]);

  const saveVideoCreditChanges = async (reset: boolean = false) => {
    setConfigLoading(true);
    const request: VideoProjectPlayerConfigRequest = reset
      ? {
          resetToDefault: true,
        }
      : {
          monthlyVideoCredits: editableCredits,
        };
    const status = await VideoProjectServices.updatePlayerConfig(playerId!!, request!!);
    toast.success("Video credits updated successfully");
    setConfigLoading(false);
    onUpdateStatus(status);
    onClose();
  };

  return (
    <Dialog
      size="sm"
      open={open}
      onOpenChange={o => {
        if (!o) onClose();
      }}
    >
      <Dialog.Header>
        <Dialog.Title>Edit Video Credits</Dialog.Title>
      </Dialog.Header>

      <Dialog.Content>
        <Field>
          <Field.Label>Monthly Credits</Field.Label>
          {vcStatus?.unlimitedCredits && <div>Unlimited Credits</div>}
          {!vcStatus?.unlimitedCredits && (
            <Input
              id="monthly-credits"
              placeholder="Monthly Credits"
              type={"number"}
              value={editableCredits}
              onChange={data => {
                setEditableCredits(data.target.valueAsNumber);
              }}
            />
          )}
        </Field>
      </Dialog.Content>

      <Dialog.Footer>
        {canResetCredits && (
          <Button
            variant="outline"
            loading={configLoading}
            onClick={() => {
              saveVideoCreditChanges(true);
            }}
          >
            Reset to default ({coachVCConfig?.defaultPlayerVideoCredits} credits)
          </Button>
        )}
        {showSaveCredits && (
          <Button
            loading={configLoading}
            disabled={!canSaveCredits}
            onClick={() => {
              saveVideoCreditChanges();
            }}
          >
            Save
          </Button>
        )}
      </Dialog.Footer>
    </Dialog>
  );
};

export const VideoCreditsModal = VideoCreditsModalFC;
